import { useCallback, useEffect, useState } from 'react';
import { Server, Cloud, HardDrive, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api-client';
import { getCloudProviderColor, getTenantColor } from '../lib/colors';
import { TenantHeading } from '../components/tenants/TenantLogo';
import { resolveTenantMeta, useTenantMetaMap } from '../hooks/useTenantMetaMap';
import { usePermission } from '../hooks/usePermission';
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat';
import { useCacheEvents } from '../hooks/useCacheEvents';
import { PageHeader } from '../components/ui/PageHeader';
import { LastSynced } from '../components/ui/LastSynced';

interface Instance {
  id: string;
  name: string;
  provider: 'oci' | 'hetzner' | 'aws';
  status: string;
  instanceType: string;
  region: string;
  publicIp?: string;
  privateIp?: string;
  created: Date | string;
  tags: Record<string, string>;
}

interface TenantInstances {
  tenant: string;
  tenantName?: string; // Display name
  tenantColor?: string | null;
  providers: {
    oci: Instance[];
    hetzner: Instance[];
    aws: Instance[];
  };
}

export function Instances() {
  const { accessibleTenants, selectedTenants } = useAuth();
  const { isAllowed: canView } = usePermission('instances', 'read');
  const tenantMetaMap = useTenantMetaMap(canView);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenantInstances, setTenantInstances] = useState<TenantInstances[]>([]);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);

  const tenantsToQuery = selectedTenants.length > 0 ? selectedTenants : accessibleTenants;

  useActivityHeartbeat('cache:tenant_servers', canView);

  const fetchInstances = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch all tenants to get their display names and colors
      const tenantsResponse = await apiClient.getTenants();
      const tenantsData = Array.isArray(tenantsResponse) ? tenantsResponse : tenantsResponse.tenants || [];
      const tenantMap = new Map(tenantsData.map((t: any) => [t.code.toLowerCase(), { name: t.name, color: t.color }]));
      
      // Fetch instances for each tenant
      const results = await Promise.all(
        tenantsToQuery.map(async (tenant) => {
          try {
            // Fetch from each provider separately
            const [ociInstances, hetznerInstances, awsInstances] = await Promise.all([
              apiClient.getInstances(tenant, 'oci').catch(() => ({ instances: [] })),
              apiClient.getInstances(tenant, 'hetzner').catch(() => ({ instances: [] })),
              apiClient.getInstances(tenant, 'aws').catch(() => ({ instances: [] })),
            ]);

            const tenantInfo = tenantMap.get(tenant.toLowerCase());
            const synced = ociInstances.syncedAt ?? hetznerInstances.syncedAt ?? awsInstances.syncedAt;
            return {
              tenant,
              tenantName: tenantInfo?.name || tenant,
              tenantColor: tenantInfo?.color,
              providers: {
                oci: ociInstances.instances || [],
                hetzner: hetznerInstances.instances || [],
                aws: awsInstances.instances || [],
              },
              syncedAt: synced,
            };
          } catch (err: any) {
            console.error(`Failed to fetch instances for ${tenant}:`, err);
            const tenantInfo = tenantMap.get(tenant.toLowerCase());
            return {
              tenant,
              tenantName: tenantInfo?.name || tenant,
              tenantColor: tenantInfo?.color,
              providers: {
                oci: [],
                hetzner: [],
                aws: [],
              },
              syncedAt: null,
            };
          }
        })
      );

      setSyncedAt(results[0]?.syncedAt ?? null);
      setTenantInstances(results.map(({ syncedAt: _, ...rest }) => rest));
    } catch (err: any) {
      console.error('Failed to fetch instances:', err);
      setError(err.message || 'Failed to load instances');
    } finally {
      setIsLoading(false);
    }
  }, [tenantsToQuery]);

  useEffect(() => {
    if (tenantsToQuery.length > 0) {
      void fetchInstances();
    } else {
      setIsLoading(false);
    }
  }, [fetchInstances, tenantsToQuery.length]);

  // Refresh the list when the backend cache updates (SSE), so server status changes appear without a page reload.
  useCacheEvents({
    enabled: canView,
    cacheNames: ['tenant_servers'],
    onEvent: () => {
      void fetchInstances();
    },
  });


  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('running') || statusLower === 'active') {
      return <Badge variant="success">Running</Badge>;
    } else if (statusLower.includes('stopped') || statusLower === 'inactive') {
      return <Badge variant="destructive">Stopped</Badge>;
    } else if (statusLower.includes('pending') || statusLower.includes('provisioning')) {
      return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800">Pending</Badge>;
    } else {
      return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'aws':
        return <Cloud className="w-4 h-4" />;
      case 'oci':
        return <HardDrive className="w-4 h-4" />;
      case 'hetzner':
        return <Server className="w-4 h-4" />;
      default:
        return <Server className="w-4 h-4" />;
    }
  };

  const getProviderName = (provider: string) => {
    switch (provider) {
      case 'aws':
        return 'AWS';
      case 'oci':
        return 'OCI';
      case 'hetzner':
        return 'Hetzner';
      default:
        return provider.toUpperCase();
    }
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const sortInstances = (instances: Instance[]) => {
    return [...instances].sort((a, b) => {
      // Normalize status to Running/Stopped
      const statusA = a.status.toLowerCase();
      const statusB = b.status.toLowerCase();
      const isRunningA = statusA.includes('running') || statusA === 'active';
      const isRunningB = statusB.includes('running') || statusB === 'active';
      
      // Running first, then stopped
      if (isRunningA && !isRunningB) return -1;
      if (!isRunningA && isRunningB) return 1;
      
      // Within same status, sort by name
      return a.name.localeCompare(b.name);
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2].map((i) => (
          <div key={i} className="space-y-4">
            <Skeleton className="h-6 w-32" />
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((j) => (
                <Card key={j}>
                  <CardHeader>
                    <Skeleton className="h-5 w-40 mb-2" />
                    <Skeleton className="h-4 w-24" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
        </div>
        </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="py-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-600 dark:text-red-400 mb-4" />
            <p className="text-red-600 dark:text-red-400">Error: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalInstances = tenantInstances.reduce((sum, ti) => 
    sum + ti.providers.oci.length + ti.providers.hetzner.length + ti.providers.aws.length, 0
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <PageHeader
          icon={Server}
          iconColor="text-blue-600 dark:text-blue-400"
          iconBgColor="bg-blue-100 dark:bg-blue-900/30"
          description="Monitor and manage tenant cloud servers"
          count={totalInstances}
          countLabel="instances"
        />
        <LastSynced iso={syncedAt} />
      </div>
      {tenantInstances.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
            No instances found for the selected tenants.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {tenantInstances.map((tenantData) => {
            const tenantTotal = tenantData.providers.oci.length + 
                              tenantData.providers.hetzner.length + 
                              tenantData.providers.aws.length;
            
            if (tenantTotal === 0) return null;

            return (
              <div key={tenantData.tenant} className="space-y-6">
                {/* Tenant Header - Prominent but clean */}
                <div className="flex items-center justify-between">
                  <TenantHeading
                    tenant={resolveTenantMeta(tenantMetaMap, tenantData.tenant, {
                      name: tenantData.tenantName,
                      color: tenantData.tenantColor,
                    })}
                    title={tenantData.tenantName || tenantData.tenant}
                  />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {tenantTotal} instance{tenantTotal !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* OCI Instances */}
                {tenantData.providers.oci.length > 0 && (
                  <div className="space-y-4 md:ml-6">
                    <div className="flex items-center gap-2 py-2 border-b border-gray-200 dark:border-gray-700">
                      {getProviderIcon('oci')}
                      <h3 className="text-base font-medium" style={{ color: getCloudProviderColor('oci') }}>
                        OCI
                      </h3>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ({tenantData.providers.oci.length})
                      </span>
                    </div>
                    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                      {sortInstances(tenantData.providers.oci).map((instance) => (
                        <Card key={instance.id} className="hover:shadow-lg transition-shadow w-full max-w-full overflow-hidden">
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center space-x-3 min-w-0 flex-1">
                                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                  {getProviderIcon('oci')}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-lg truncate">{instance.name}</CardTitle>
                                  <CardDescription className="font-mono text-xs mt-1 truncate">
                                    {instance.id.length > 30 ? `${instance.id.substring(0, 30)}...` : instance.id}
                                  </CardDescription>
                                </div>
                              </div>
                              <div className="flex-shrink-0">
                                {getStatusBadge(instance.status)}
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between gap-2">
                                <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Type:</span>
                                <span className="font-mono text-gray-900 dark:text-gray-100 truncate text-right">{instance.instanceType}</span>
                              </div>
                              <div className="flex justify-between gap-2">
                                <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Region:</span>
                                <span className="text-gray-900 dark:text-gray-100 truncate text-right">{instance.region}</span>
                              </div>
                              {instance.privateIp && (
                                <div className="flex justify-between gap-2">
                                  <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Private IP:</span>
                                  <span className="font-mono text-gray-900 dark:text-gray-100 truncate text-right">{instance.privateIp}</span>
                                </div>
                              )}
                              {instance.publicIp && (
                                <div className="flex justify-between gap-2">
                                  <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Public IP:</span>
                                  <span className="font-mono text-gray-900 dark:text-gray-100 truncate text-right">{instance.publicIp}</span>
                                </div>
                              )}
                              <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
                                Created {formatDate(instance.created)}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Hetzner Instances */}
                {tenantData.providers.hetzner.length > 0 && (
                  <div className="space-y-4 md:ml-6">
                    <div className="flex items-center gap-2 py-2 border-b border-gray-200 dark:border-gray-700">
                      {getProviderIcon('hetzner')}
                      <h3 className="text-base font-medium" style={{ color: getCloudProviderColor('hetzner') }}>
                        Hetzner
                      </h3>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ({tenantData.providers.hetzner.length})
                      </span>
                    </div>
                    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                      {sortInstances(tenantData.providers.hetzner).map((instance) => (
                        <Card key={instance.id} className="hover:shadow-lg transition-shadow w-full max-w-full overflow-hidden">
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center space-x-3 min-w-0 flex-1">
                                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                  {getProviderIcon('hetzner')}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-lg truncate">{instance.name}</CardTitle>
                                  <CardDescription className="font-mono text-xs mt-1 truncate">
                                    ID: {instance.id}
                                  </CardDescription>
                                </div>
                              </div>
                              <div className="flex-shrink-0">
                                {getStatusBadge(instance.status)}
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between gap-2">
                                <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Type:</span>
                                <span className="font-mono text-gray-900 dark:text-gray-100 truncate text-right">{instance.instanceType}</span>
                              </div>
                              <div className="flex justify-between gap-2">
                                <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Datacenter:</span>
                                <span className="text-gray-900 dark:text-gray-100 truncate text-right">{instance.region}</span>
                              </div>
                              {instance.privateIp && (
                                <div className="flex justify-between gap-2">
                                  <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Private IP:</span>
                                  <span className="font-mono text-gray-900 dark:text-gray-100 truncate text-right">{instance.privateIp}</span>
                                </div>
                              )}
                              {instance.publicIp && (
                                <div className="flex justify-between gap-2">
                                  <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Public IP:</span>
                                  <span className="font-mono text-gray-900 dark:text-gray-100 truncate text-right">{instance.publicIp}</span>
                                </div>
                              )}
                              <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
                                Created {formatDate(instance.created)}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* AWS Instances */}
                {tenantData.providers.aws.length > 0 && (
                  <div className="space-y-4 md:ml-6">
                    <div className="flex items-center gap-2 py-2 border-b border-gray-200 dark:border-gray-700">
                      {getProviderIcon('aws')}
                      <h3 className="text-base font-medium" style={{ color: getCloudProviderColor('aws') }}>
                        AWS
                      </h3>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ({tenantData.providers.aws.length})
                      </span>
                    </div>
                    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                      {sortInstances(tenantData.providers.aws).map((instance) => (
                        <Card key={instance.id} className="hover:shadow-lg transition-shadow w-full max-w-full overflow-hidden">
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center space-x-3 min-w-0 flex-1">
                                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                  {getProviderIcon('aws')}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-lg truncate">{instance.name}</CardTitle>
                                  <CardDescription className="font-mono text-xs mt-1 truncate">
                                    {instance.id}
                                  </CardDescription>
                                </div>
                              </div>
                              <div className="flex-shrink-0">
                                {getStatusBadge(instance.status)}
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between gap-2">
                                <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Type:</span>
                                <span className="font-mono text-gray-900 dark:text-gray-100 truncate text-right">{instance.instanceType}</span>
                              </div>
                              <div className="flex justify-between gap-2">
                                <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">AZ:</span>
                                <span className="text-gray-900 dark:text-gray-100 truncate text-right">{instance.region}</span>
                              </div>
                              {instance.privateIp && (
                                <div className="flex justify-between gap-2">
                                  <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Private IP:</span>
                                  <span className="font-mono text-gray-900 dark:text-gray-100 truncate text-right">{instance.privateIp}</span>
                                </div>
                              )}
                              {instance.publicIp && (
                                <div className="flex justify-between gap-2">
                                  <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Public IP:</span>
                                  <span className="font-mono text-gray-900 dark:text-gray-100 truncate text-right">{instance.publicIp}</span>
                                </div>
                              )}
                              <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
                                Created {formatDate(instance.created)}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
